import { Check, Pencil, Sparkles } from 'lucide-react'
import type { Category, CatalogItem, WizardForm } from './types'

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

/**
 * Passo 7 — a conferida final antes de gerar.
 *
 * Existe por um motivo específico: gerar é o passo que COBRA e que dispara a
 * IA, e até aqui a pessoa preencheu sete telas sem nunca ver tudo junto. Cada
 * linha leva de volta ao passo que a preencheu — corrigir não pode custar
 * refazer o caminho.
 *
 * O plano e o cupom não estão aqui: vivem no resumo de valor (a coluna à
 * direita no desktop, a gaveta no celular), que acompanha a pessoa desde o
 * primeiro passo.
 */
export default function StepReview({
  data,
  categories,
  items,
  audienceLabels,
  hasCover,
  photoCount,
  videoCount,
  onEditStep,
}: {
  data: WizardForm
  categories: Category[]
  items: CatalogItem[]
  audienceLabels: string[]
  /**
   * A mídia do NEGÓCIO (passo 5) — as fotos do catálogo contam na linha dele.
   * `photoCount` inclui a capa: senão uma página só com capa apareceria aqui
   * como "nenhuma foto".
   */
  hasCover: boolean
  photoCount: number
  videoCount: number
  onEditStep: (step: number) => void
}) {
  const services = categories
    .flatMap((c) => c.subcategories)
    .filter((s) => data.subcategory_ids.includes(s.id))

  const local = [data.city, data.state].filter(Boolean).join('/')
  const dias = data.operating_hours.days
    .map((d) => DAY_LABELS[d])
    .filter(Boolean)
    .join(', ')

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-5">
        <p className="flex items-center gap-2 text-sm font-bold text-gray-900">
          <Sparkles className="size-4 text-primary" aria-hidden="true" />
          Nossa IA vai escrever sua página
        </p>
        <p className="mt-1.5 text-sm text-gray-600">
          Com o que você preencheu, ela monta o título, a apresentação, os
          destaques e o texto de cada item do seu catálogo. Depois você revisa e
          edita o que quiser.
        </p>
      </div>

      <dl className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200">
        <Row
          label="O que você oferece"
          value={services.map((s) => s.label).join(', ')}
          onEdit={() => onEditStep(1)}
        />
        <Row
          label="Negócio"
          value={[data.name, local].filter(Boolean).join(' · ')}
          onEdit={() => onEditStep(2)}
        />
        <Row
          label="Contato"
          value={data.whatsapp}
          onEdit={() => onEditStep(3)}
        />
        <Row
          label="Catálogo"
          value={
            items.length === 0
              ? null
              : `${items.length} ${items.length === 1 ? 'item' : 'itens'} · ${
                  items.filter((i) => i.kind === 'produto').length
                } produto(s), ${
                  items.filter((i) => i.kind === 'servico').length
                } serviço(s)`
          }
          empty="Nenhum item — sua página fica sem a vitrine"
          onEdit={() => onEditStep(4)}
        />
        <Row
          label="Fotos e vídeos"
          value={
            photoCount === 0 && videoCount === 0
              ? null
              : [
                  photoCount > 0 &&
                    `${photoCount} ${photoCount === 1 ? 'foto' : 'fotos'}${
                      hasCover ? ', com capa' : ' — sem capa'
                    }`,
                  videoCount > 0 &&
                    `${videoCount} ${videoCount === 1 ? 'vídeo' : 'vídeos'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
          empty="Nenhuma foto — seu card fica sem imagem na busca"
          onEdit={() => onEditStep(5)}
        />
        <Row
          label="Horários"
          value={dias || null}
          empty="Sem horário publicado"
          onEdit={() => onEditStep(6)}
        />
        <Row
          label="Público"
          value={audienceLabels.join(', ')}
          onEdit={() => onEditStep(7)}
        />
      </dl>
    </div>
  )
}

function Row({
  label,
  value,
  empty,
  onEdit,
}: {
  label: string
  value: string | null
  /** O que dizer quando está vazio — e vazio aqui nunca é erro, só falta. */
  empty?: string
  onEdit: () => void
}) {
  const preenchido = Boolean(value)

  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <span
        aria-hidden="true"
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full ${
          preenchido ? 'bg-primary text-primary-foreground' : 'bg-gray-200'
        }`}
      >
        {preenchido && <Check className="size-3" strokeWidth={3} />}
      </span>

      <div className="min-w-0 flex-1">
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {label}
        </dt>
        <dd
          className={`mt-0.5 break-words text-sm ${
            preenchido ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {value ?? empty ?? '—'}
        </dd>
      </div>

      <button
        type="button"
        onClick={onEdit}
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
      >
        <Pencil className="size-3" aria-hidden="true" />
        Editar
      </button>
    </div>
  )
}
